// The Receipts page's calls: one Netlify function (netlify/functions/
// receipts.js), POST { op, ... } with the Microsoft ID token, like the
// FamilyVault B2 calls. On a 401, once more with a fresh token.
import { msalInstance, graphRequest } from '../../msalConfig';

const URL = '/api/receipts';

async function getIdToken(forceRefresh) {
  const account = msalInstance.getAllAccounts()[0];
  if (!account) throw new Error('Not signed in');
  const response = await msalInstance.acquireTokenSilent({ ...graphRequest, account, forceRefresh: !!forceRefresh });
  return response.idToken;
}

/** POST one op; resolves to the JSON body, rejects with the server's error text. */
export async function receiptsCall(op, payload = {}) {
  const send = async (forceRefresh) => fetch(URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await getIdToken(forceRefresh)}` },
    body: JSON.stringify({ ...payload, op }),
  });
  let res = await send(false);
  if (res.status === 401) res = await send(true);
  const text = await res.text();
  let body = null;
  try { body = text ? JSON.parse(text) : null; } catch { body = null; }
  if (!res.ok) {
    const e = new Error(body?.error || `receipts ${op} failed (${res.status})`);
    e.status = res.status;
    e.body = body;
    throw e;
  }
  return body;
}

// Where a draft landed, in words. The server reports the mailbox.
const MAILBOX_LABELS = {
  'marc@defineholy.com': "Marc's Outlook",
  'marc@myfiginsurance.com': "Marc's FIG Outlook",
};
export const mailboxLabel = (upn) => MAILBOX_LABELS[String(upn || '').toLowerCase()] || upn || 'Outlook';

/**
 * Download each receipt's original file(s): one file each (no zip). Each
 * download link is short-lived, scoped to one file, and served as an
 * attachment, so a hidden frame saves it without leaving the page. A browser
 * may ask once to allow several downloads.
 * -> { files, failed: [ids] }
 */
export async function downloadReceipts(ids) {
  let files = 0;
  const failed = [];
  for (const id of ids) {
    try {
      const d = await receiptsCall('detail', { id });
      for (const f of d.files || []) {
        if (!f.downloadUrl) continue;
        const frame = document.createElement('iframe');
        frame.style.display = 'none';
        frame.src = f.downloadUrl;
        document.body.appendChild(frame);
        setTimeout(() => frame.remove(), 60_000);
        files++;
      }
    } catch {
      failed.push(id);
    }
  }
  return { files, failed };
}

/**
 * One zip of the receipts' originals. The server checks the cap (50
 * receipts / 100 MB), logs it, and answers with a one-time link (five
 * minutes) that this opens in a hidden frame; the zip streams straight from
 * the server. -> { name, receipts, files, bytes }
 */
export async function downloadZip(ids) {
  const out = await receiptsCall('zip', { ids });
  const frame = document.createElement('iframe');
  frame.style.display = 'none';
  frame.src = out.url;
  document.body.appendChild(frame);
  setTimeout(() => frame.remove(), 5 * 60_000);
  return out;
}
